import { EmptyState, PageHeader, Skeleton } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { RoleForm } from '@/features/roles/RoleForm';
import { useRoles } from '@/features/roles/hooks';
import { useNavigate, useParams } from '@solidjs/router';
import type { RoleListItem } from '@uchat-team-management/shared';
import { Show } from 'solid-js';

/** Create (`/admin/roles/new`) and edit (`/admin/roles/:roleId`) share this page. */
export default function AdminRoleForm() {
  const params = useParams<{ roleId?: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();
  const roles = useRoles();

  const isEdit = () => params.roleId !== undefined;
  const can = (key: string) => session()?.permissions.has(key) ?? false;
  // Any role is viewable with roles:list; editing needs roles:update, and the
  // system admin role stays read-only for everyone (server enforces the same).
  const allowed = () => can(isEdit() ? 'roles:list' : 'roles:create');
  const role = () => roles.data?.data.find((r) => r.id === params.roleId);
  const readOnly = (r: RoleListItem) => (r.isSystem && r.name === 'admin') || !can('roles:update');
  const done = () => navigate('/admin/roles');

  const title = () => {
    const r = role();
    if (!isEdit()) return 'New role';
    if (!r) return 'Edit role';
    return readOnly(r) ? `Role: ${r.name}` : `Edit role: ${r.name}`;
  };

  return (
    <section>
      <PageHeader title={title()} />
      <div class="mx-auto max-w-6xl p-6">
        <Show
          when={allowed()}
          fallback={
            <EmptyState
              variant="error"
              title="No permission"
              body="You don't have permission to manage roles."
            />
          }
        >
          <div class="rounded-xl border border-neutral-100 bg-surface p-6 shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
            <Show
              when={!isEdit()}
              fallback={
                <Show when={!roles.isPending} fallback={<Skeleton class="h-60" />}>
                  <Show
                    when={role()}
                    fallback={<EmptyState variant="error" title="Role not found" />}
                  >
                    {(r) => (
                      <RoleForm
                        role={r()}
                        readOnly={readOnly(r())}
                        nameLocked={r().isSystem}
                        onDone={done}
                        onCancel={done}
                      />
                    )}
                  </Show>
                </Show>
              }
            >
              <RoleForm onDone={done} onCancel={done} />
            </Show>
          </div>
        </Show>
      </div>
    </section>
  );
}
